import { apiSlice } from 'store/slices/api';
import {
  ClientV2PATCHResponse,
  ClientV2POSTRequest,
  ListResponse,
  QueryParams,
} from 'types/api';
import { CLIENT_V2_URLS } from 'store/slices/constants/clientV2';
import { parseQueryParams } from 'store/slices/utils';
import {
  BomLineManageAltActionRequest,
  BomLineSchema,
  BomSchema,
} from 'types/clientV2/boms';

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getClientV2Boms: builder.query<
      ListResponse<BomSchema>,
      QueryParams<BomSchema>
    >({
      query: (params) => ({
        url: CLIENT_V2_URLS.BOMS(parseQueryParams<BomSchema>(params)),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: BomSchema) =>
                  ({
                    type: 'Boms',
                    id,
                  } as const)
              ),
              { type: 'Boms', id: 'LIST' },
            ]
          : [{ type: 'Boms', id: 'LIST' }],
    }),
    getClientV2BomLines: builder.query<
      ListResponse<BomLineSchema>,
      QueryParams<BomLineSchema>
    >({
      query: (params) => ({
        url: CLIENT_V2_URLS.BOM_LINES(parseQueryParams<BomLineSchema>(params)),
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }: BomLineSchema) =>
                  ({
                    type: 'BomLines',
                    id,
                  } as const)
              ),
              { type: 'BomLines', id: 'LIST' },
            ]
          : [{ type: 'BomLines', id: 'LIST' }],
    }),
    bomLineAddAlts: builder.mutation<
      ClientV2PATCHResponse<BomLineSchema>,
      ClientV2POSTRequest<BomLineManageAltActionRequest>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.BOM_LINES_ADD_APPROVED_ALTS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              ...result.updatedIds.map(
                (id: BomLineSchema['id']) =>
                  ({
                    type: 'BomLines',
                    id,
                  } as const)
              ),
              { type: 'BomLines', id: 'LIST' },
            ]
          : [{ type: 'BomLines', id: 'LIST' }],
    }),
    bomLineRemoveApprovedAlts: builder.mutation<
      ClientV2PATCHResponse<BomLineSchema>,
      ClientV2POSTRequest<BomLineManageAltActionRequest>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.BOM_LINES_REMOVE_APPROVED_ALTS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              ...result.updatedIds.map(
                (id: BomLineSchema['id']) =>
                  ({
                    type: 'BomLines',
                    id,
                  } as const)
              ),
              { type: 'BomLines', id: 'LIST' },
            ]
          : [{ type: 'BomLines', id: 'LIST' }],
    }),
    bomLineAddUnapprovedAlts: builder.mutation<
      ClientV2PATCHResponse<BomLineSchema>,
      ClientV2POSTRequest<BomLineManageAltActionRequest>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.BOM_LINES_ADD_UNAPPROVED_ALTS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              ...result.updatedIds.map(
                (id: BomLineSchema['id']) =>
                  ({
                    type: 'BomLines',
                    id,
                  } as const)
              ),
              { type: 'BomLines', id: 'LIST' },
            ]
          : [{ type: 'BomLines', id: 'LIST' }],
    }),
    bomLineRemoveUnapprovedAlts: builder.mutation<
      ClientV2PATCHResponse<BomLineSchema>,
      ClientV2POSTRequest<BomLineManageAltActionRequest>
    >({
      query: (data) => ({
        url: CLIENT_V2_URLS.BOM_LINES_REMOVE_UNAPPROVED_ALTS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result) =>
        result
          ? [
              ...result.updatedIds.map(
                (id: BomLineSchema['id']) =>
                  ({
                    type: 'BomLines',
                    id,
                  } as const)
              ),
              { type: 'BomLines', id: 'LIST' },
            ]
          : [{ type: 'BomLines', id: 'LIST' }],
    }),
  }),
});

export const {
  usePrefetch,
  useGetClientV2BomsQuery,
  useGetClientV2BomLinesQuery,
  useBomLineAddAltsMutation,
  useBomLineRemoveApprovedAltsMutation,
  useBomLineAddUnapprovedAltsMutation,
  useBomLineRemoveUnapprovedAltsMutation,
} = extendedApiSlice;
